import axios from 'axios';
import moment from 'moment';
import downloadFile from 'downloadjs';
import { isEmpty, range } from 'lodash';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { fetchInfluencersMeta } from '../../influencers/reducers/influencersMeta';
import { fetchCreatorApprovalStatus } from 'modules/proposal/reducers/creator-proposal-status';
import { fetchInfluencerResponseTime } from 'modules/influencers/reducers/influencerResponseTime';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post, put, request } from 'utils/api';
import { Querystring } from 'utils/url';
import { logError } from 'utils/log';
import { prepareResultsForSavingToList } from 'modules/discovery/creators/utils/queries';
import {
  DEFAULT_SORT,
  LIST_DASHBOARD_SORT_OPTIONS,
  RELEVANCE_SORT,
  SMART_LIST_TYPE,
  isMyCreatedProfileSmartlist
} from 'utils/list';
import { __t, applyTranslations } from 'utils/language';

import type {
  Action,
  DiscoveryCreatorsFilters,
  Dispatch,
  GetState,
  List,
  ListMeta,
  ListType,
  PaginationLinks,
  PaginationMeta,
  ThunkAction
} from 'types';
import type { ListImportTask, ListState } from 'types/List.type';
import type { AxiosRequestConfig } from 'axios';

applyTranslations(require('./lists-reducer.i18n.json'));

const { CONTENT_LISTS, CREATOR_PROPOSAL, LISTS } = API.END_POINTS;

const LOADING = 'lists/LOADING';
const AGGREGATE_LOADING = 'lists/AGGREGATE_LOADING';
const LIST_LOADED = 'lists/LIST_LOADED';
const LIST_AGGREGATES_LOADED = 'lists/LIST_AGGREGATES_LOADED';
const RESET_LIST_AGGREGATES = 'lists/RESET_LIST_AGGREGATES';
const DASHBOARD_LOADING = 'lists/DASHBOARD_LOADING';
const DASHBOARD_SORT_SEARCH_LOADING = 'lists/DASHBOARD_SORT_SEARCH_LOADING';
const DASHBOARD_LOADED = 'lists/DASHBOARD_LOADED';
const UPDATED = 'lists/UPDATED';
const DELETED = 'lists/DELETED';
const INFLUENCERS_LOADED = 'lists/INFLUENCERS_LOADED';
const INFLUENCER_ADDED = 'lists/INFLUENCER_ADDED';
const INFLUENCER_REMOVED = 'lists/INFLUENCER_REMOVED';
const SINGLE_INFLUENCER_REMOVED = 'lists/SINGLE_INFLUENCER_REMOVED';
const HISTORY_LOADED = 'lists/HISTORY_LOADED';
const UPDATE_SCREEN_STATE = 'lists/UPDATE_SCREEN_STATE';
const UPDATE_SCREEN_STATE_PAGINATION = 'lists/UPDATE_SCREEN_STATE_PAGINATION';
const ADD_LOADING_ITEMS = 'lists/ADD_LOADING_ITEMS';
const RESET_LOADING_ITEMS = 'lists/RESET_LOADING_ITEMS';
const RESET_LISTS = 'lists/RESET_LISTS';
const RESET_INFLUENCERS = 'lists/RESET_INFLUENCERS';
const UPDATE_CREATED_PROFILE_SMARTLIST_ID = 'lists/UPDATE_CREATED_PROFILE_SMARTLIST_ID';
const SAVING_SEARCH_TO_LIST = 'lists/SAVING_SEARCH_TO_LIST';
const UPDATE_LIGHTWEIGHT_INFLUENCERS_IMPORT_TASKS = 'lists/UPDATE_LIGHTWEIGHT_INFLUENCERS_IMPORT_TASKS';

type Options = {
  loadMore?: boolean,
  loadCreatorApprovalStatus?: boolean
};

const initialState: ListState = {
  all: [],
  currentList: {
    list: {} as List,
    influencers: [],
    isLoading: false,
    aggregateData: {
      isLoading: true
    }
  },
  historyAvailability: {},
  screenState: {
    page: 0,
    totalPages: 0,
    totalItems: 0,
    sort: DEFAULT_SORT,
    pagination: {}
  },
  isLoading: false,
  isSearchingOrSorting: false,
  isSavingToList: false,
  myCreatedProfileSmartlistId: null
};

const ITEMS_PER_PAGE = 10;

function influencersLoaded(data: Array<List>, loadMore: boolean = false): Action {
  return {
    type: INFLUENCERS_LOADED,
    payload: {
      data,
      loadMore
    }
  };
}

function influencerAdded(influencerId, listId): Action {
  return {
    type: INFLUENCER_ADDED,
    payload: {
      influencerId,
      listId
    }
  };
}

function influencerRemoved(influencerId, listId, currentListId): Action {
  return {
    type: INFLUENCER_REMOVED,
    payload: {
      influencerId,
      listId,
      currentListId
    }
  };
}

function influencersHistoryLoaded(data): Action {
  return {
    type: HISTORY_LOADED,
    payload: {
      data
    }
  };
}

const dashboardListsLoaded = (results: Array<List>): Action => ({
  type: DASHBOARD_LOADED,

  payload: {
    results
  }
});

const listLoaded = (list: List): Action => ({
  type: LIST_LOADED,

  payload: {
    list
  }
});

const singleInfluencerRemoved = (listId: number, influencerId: number): Action => ({
  type: SINGLE_INFLUENCER_REMOVED,

  payload: {
    listId,
    influencerId
  }
});

function listsUpdated(data: List, shouldUpdate: boolean): Action {
  return {
    type: UPDATED,
    payload: {
      data,
      shouldUpdate
    }
  };
}

function listDeleted(id: number): Action {
  return {
    type: DELETED,
    payload: {
      id
    }
  };
}

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

export function resetLists(): Action {
  return {
    type: RESET_LISTS,
    payload: {}
  };
}

export function updateScreenState(value: number | string, attribute: string): Action {
  return {
    type: UPDATE_SCREEN_STATE,
    payload: {
      value,
      attribute
    }
  };
}

function updateScreenStatePagination(meta: PaginationMeta, links: PaginationLinks): Action {
  return {
    type: UPDATE_SCREEN_STATE_PAGINATION,
    payload: {
      meta,
      links
    }
  };
}

function updateMyCreatedProfileSmartlistId(id?: number | null) {
  return {
    type: UPDATE_CREATED_PROFILE_SMARTLIST_ID,
    payload: {
      id
    }
  };
}

export function updateLightweightInfluencersImportTasks(
  lightweightInfluencersImportTasks: Array<ListImportTask>
) {
  return {
    type: UPDATE_LIGHTWEIGHT_INFLUENCERS_IMPORT_TASKS,
    payload: { lightweightInfluencersImportTasks }
  };
}

export function resetMyCreatedProfileSmartlistId() {
  return updateMyCreatedProfileSmartlistId(null);
}

// this is what allows loading cards to appear
function exposeLoading(state: string, count: number): ThunkAction {
  const loadingItems = count ? range(count) : [0];

  return (dispatch: Dispatch) => {
    dispatch({
      type: state,
      payload: {
        loadingItems: loadingItems.map(item => (
          {
            id: String(item),
            loading: true
          }
        ))
      }
    });
  };
}

export function downloadList(organizationId: number, listId: number, label: string, type: ListType) {
  const url = `${LISTS.getListInfluencers(organizationId, listId)}/export`;
  const requestConfig: AxiosRequestConfig = {
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    responseType: 'blob'
  };
  const date = moment().format('YYYY-MM-DD');
  const fileName = `Collabary Creator List ${date} (${label}).xlsx`;

  return (dispatch: Dispatch) => {
    dispatch(showLoading(url));

    return get(url, requestConfig)
      .then((response) => {
        downloadFile(response.data, fileName);
        dispatch(hideLoading(url));
        addUserAction({
          eventCategory: 'collections',
          eventAction: 'creators-downloaded',
          eventLabel: `collection.${listId}`,
          customDimensionData: {
            collectionInfo: {
              type,
              id: listId,
              name: label
            }
          }
        });
      })
      .catch(() => dispatch(hideLoading(url)));
  };
}

export function fetchInfluencersHistory(organizationId: number, influencerIds: Array<number> = []): ThunkAction {
  const url = LISTS.getInfluencersHistory(organizationId, influencerIds);

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(LOADING, true));

    return get(url)
      .then(response => {
        dispatch(influencersHistoryLoaded(response.data));
        dispatch(setLoadingStatus(LOADING, false));
      })
      .catch(() => {
        dispatch(setLoadingStatus(LOADING, false));
      });
  };
}

export function fetchListInfluencers(
  organizationId: number,
  listId: number,
  searchByNameValue: string = '',
  pageOverride: number | undefined | null = null,
  options: Options = {
    loadMore: false,
    loadCreatorApprovalStatus: false
  }
): ThunkAction {
  return (dispatch: Dispatch, getState: GetState) => {
    if (isNaN(organizationId)) {
      return Promise.resolve();
    }

    const listsState = getState().lists;
    const screenState = listsState.screenState;
    const screenStatePage = screenState.page === 0 ? screenState.page + 1 : screenState.page;
    const currentPage = pageOverride || screenStatePage;
    const sortValue = !isEmpty(searchByNameValue) ? RELEVANCE_SORT : screenState.sort;
    const sort = `&sort=${sortValue}`;
    const searchByName = searchByNameValue ? `&name=${searchByNameValue}` : '';

    const params = {
      page: currentPage,
      items_per_page: ITEMS_PER_PAGE
    };

    const queryString = `${Querystring.objectToString(params)}${searchByName}${sort}&`;
    const url = `${LISTS.getListInfluencers(organizationId, listId)}?${queryString}`;

    dispatch(setLoadingStatus(LOADING, true));
    dispatch(exposeLoading(options.loadMore ? ADD_LOADING_ITEMS : RESET_LOADING_ITEMS, 1));

    // these needs to be inside the ".then" but "influencers" is mounting and
    // unmounting two times each time and that means for timing we end up in page 2
    dispatch(updateScreenState(currentPage, 'page'));

    return get(url)
      .then(response => {
        const results = response.data;
        const meta = results.meta;

        dispatch(influencersLoaded(results.data, options.loadMore));
        dispatch(setLoadingStatus(LOADING, false));
        dispatch(updateScreenStatePagination(meta, results.links));
        dispatch(updateScreenState(meta.itemsTotal, 'totalItems'));
        dispatch(updateScreenState(meta.pagesTotal, 'totalPages'));
        dispatch(hideLoading(url));

        if (options.loadCreatorApprovalStatus) {
          const influencerIds = results.data.map(influencer => influencer.id);
          if (influencerIds.length) {
            dispatch(fetchCreatorApprovalStatus(organizationId, listId, influencerIds));
          }
        }

        const listInfluencers = response.data.data.map(influencer => influencer.id);
        if (listInfluencers.length > 0) {
          // when viewing lists (above), we need the org ID that is contained in the URL
          // however, when opening the "lists" modal, we need the logged in org ID.
          const loggedInOrgId = getState().organizations.currentOrganization?.id;

          dispatch(fetchInfluencersMeta(listInfluencers));
          if (loggedInOrgId) {
            dispatch(fetchInfluencersHistory(loggedInOrgId, listInfluencers));
          }
          dispatch(fetchInfluencerResponseTime(listInfluencers));
          dispatch(setLoadingStatus(LOADING, false));
        }
      })
      .catch(() => {
        dispatch(setLoadingStatus(LOADING, false));
      });
  };
}

export function fetchMyCreatedProfileSmartlist(orgId: number) {
  return (dispatch: Dispatch) => {
    const sortByMostRecent = LIST_DASHBOARD_SORT_OPTIONS.MOST_RECENT.value;
    const url = `${LISTS.getLists(orgId)}?type=${SMART_LIST_TYPE}&filter=brand&page=1&sortBy=${sortByMostRecent}&items_per_page=10&exclude_metrics=true`;

    get(url).then(response => {
      const results = response.data;
      const myCreatedProfileSmartlist = (results.data || []).find(isMyCreatedProfileSmartlist);

      if (myCreatedProfileSmartlist) {
        dispatch(updateMyCreatedProfileSmartlistId(myCreatedProfileSmartlist.id));
      }
    }).catch(e => {
      logError(e);
    });
  };
}

export const fetchList = (orgId: number, listId: number, channel?: string | null): ThunkAction => async (dispatch: Dispatch) => { // eslint-disable-line consistent-return
  if (isNaN(orgId)) {
    setTimeout(() => dispatch(danger(__t('lists_reducer.missing_org_id'))), 1000);
    return Promise.resolve();
  }

  dispatch(setLoadingStatus(LOADING, true));

  try {
    const url = LISTS.getList(orgId, listId, channel);
    const response = await get(url);
    const list = response.data || {};

    return dispatch(listLoaded(list));
  } catch (e) {
    logError(e);
  } finally {
    dispatch(setLoadingStatus(LOADING, false));
  }
};

let genericFetchDashboardListsCancelToken;

const genericFetchDashboardLists = (
  url: string,
  isSearchingOrSorting: boolean
): ThunkAction => async (dispatch: Dispatch): Promise<Array<List>> => {
  const loadingType = isSearchingOrSorting ? DASHBOARD_SORT_SEARCH_LOADING : DASHBOARD_LOADING;
  dispatch(setLoadingStatus(loadingType, true));

  if (genericFetchDashboardListsCancelToken) {
    genericFetchDashboardListsCancelToken.cancel();
  }

  genericFetchDashboardListsCancelToken = axios.CancelToken.source();

  try {
    const response = await get(
      url,
      { cancelToken: genericFetchDashboardListsCancelToken.token },
      { suppressGlobalNotification: true }
    );
    const lists = response.data || [];

    dispatch(setLoadingStatus(loadingType, false));
    dispatch(dashboardListsLoaded(lists));

    return lists?.data ?? [];
  } catch (e) {
    if (!axios.isCancel(e)) {
      dispatch(setLoadingStatus(loadingType, false));
      logError(e);
    }

    return [];
  }
};

export function fetchDashboardLists(
  orgId: number,
  type: string,
  filter: string,
  page: number,
  sort: string,
  search: string,
  perPage: number,
  isSearchingOrSorting: boolean
): ThunkAction {
  const url = LISTS.getDashboardLists(orgId, type, filter, page, sort, perPage, search);
  return genericFetchDashboardLists(url, isSearchingOrSorting);
}


export function fetchDashboardContentLists(
  orgId: number,
  type: string,
  filter: string,
  page: number,
  sort: string,
  search: string,
  perPage: number,
  isSearchingOrSorting: boolean
): ThunkAction {
  const url = CONTENT_LISTS.getDashboardLists(orgId, type, filter, page, sort, perPage, search);
  return genericFetchDashboardLists(url, isSearchingOrSorting);
}

export function saveList(
  list: ListMeta,
  organizationId: number,
  listId?: number,
  shouldUpdate: boolean = false
): ThunkAction {
  const url = shouldUpdate ?
    LISTS.getList(organizationId, listId) :
    LISTS.getListsV2(organizationId);

  const method = shouldUpdate ? 'PUT' : 'POST';

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(LOADING, true));
    dispatch(showLoading(url));

    return request(method, url, {
      data: {
        ...list,
        organizationId
      }
    })
      .then((response: { data: List }) => {
        dispatch(listsUpdated(response.data, shouldUpdate));
        dispatch(hideLoading(url));
        dispatch(setLoadingStatus(LOADING, false));

        return response.data;
      })
      .catch(error => {
        dispatch(setLoadingStatus(LOADING, false));
        dispatch(hideLoading(url));
        throw error;
      });
  };
}

export const saveCreatorsToList = (
  label: string,
  description: string,
  orgId: number,
  filters: DiscoveryCreatorsFilters,
  sort: string
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setLoadingStatus(SAVING_SEARCH_TO_LIST, true));
  dispatch(setLoadingStatus(LOADING, true));

  try {
    const payload = {
      label,
      description,
      organizationId: orgId,
      searchFilters: {
        ...prepareResultsForSavingToList(filters),
        sort
      }
    };

    const url = LISTS.getListsV2(orgId);
    const response = await post(url, { data: payload });
    const list = response.data || {};

    dispatch(setLoadingStatus(SAVING_SEARCH_TO_LIST, false));
    dispatch(setLoadingStatus(LOADING, false));
    return dispatch(listsUpdated(list, false));
  } catch (e) {
    dispatch(setLoadingStatus(SAVING_SEARCH_TO_LIST, false));
    dispatch(setLoadingStatus(LOADING, false));
    throw e;
  }
};

export function deleteList(organizationId: number, listId: number): ThunkAction {
  const url = LISTS.getList(organizationId, listId);

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(LOADING, true));
    dispatch(showLoading(url));

    return destroy(url)
      .then(response => {
        dispatch(hideLoading(url));
        dispatch(setLoadingStatus(LOADING, false));
        return dispatch(listDeleted(response.data.id));
      })
      .catch(error => {
        dispatch(setLoadingStatus(LOADING, false));
        dispatch(hideLoading(url));
        throw error;
      });
  };
}

export const removeSingleInfluencerFromList = (orgId: number, listId: number, influencerId: number): ThunkAction => async (dispatch: Dispatch) => { // eslint-disable-line consistent-return
  const url = `${LISTS.getListInfluencers(orgId, listId)}/${influencerId}`;

  dispatch(setLoadingStatus(LOADING, true));
  dispatch(showLoading(url));

  try {
    await destroy(url);
    dispatch(hideLoading(url));
    dispatch(setLoadingStatus(LOADING, false));
    return dispatch(singleInfluencerRemoved(listId, influencerId));
  } catch (e) {
    dispatch(setLoadingStatus(LOADING, false));
    logError(e);
  }
};

export function addInfluencerToLists(
  listIds: Array<number | string | undefined>,
  organizationId: number,
  influencerId: number,
  copyFromListId?: number | null
): ThunkAction {
  return (dispatch: Dispatch) => (
    Promise.all(listIds.map(listId => {
      const url = LISTS.getListInfluencers(organizationId, listId);
      const data = { creatorId: influencerId, copyFromListId };

      dispatch(setLoadingStatus(LOADING, true));
      dispatch(showLoading(url));

      return post(url, { data })
        .then(() => {
          dispatch(influencerAdded(influencerId, listId));
          dispatch(hideLoading(url));
          dispatch(setLoadingStatus(LOADING, false));
        })
        .catch(error => {
          dispatch(setLoadingStatus(LOADING, false));
          dispatch(hideLoading(url));
          throw error;
        });
    }))
  );
}

export function removeInfluencerFromLists(
  listIds: Array<number | string | undefined>,
  organizationId: number,
  influencerId: number,
  currentListId?: string | null
): ThunkAction {
  return (dispatch: Dispatch) => (
    Promise.all(listIds.map(listId => {
      const url = `${LISTS.getListInfluencers(organizationId, listId)}/${influencerId}`;

      dispatch(setLoadingStatus(LOADING, true));
      dispatch(showLoading(url));

      return destroy(url)
        .then(() => {
          dispatch(influencerRemoved(influencerId, listId, currentListId));
          dispatch(hideLoading(url));
          dispatch(setLoadingStatus(LOADING, false));
        })
        .catch(error => {
          dispatch(setLoadingStatus(LOADING, false));
          dispatch(hideLoading(url));
          throw error;
        });
    }))
  );
}

// Stats
const listAggregatesLoaded = (listId: number, aggregateData: {}): Action => ({
  type: LIST_AGGREGATES_LOADED,

  payload: {
    listId,
    aggregateData
  }
});

export const resetListAggregates = (): Action => ({
  type: RESET_LIST_AGGREGATES
});

export const fetchStats = (orgId: number, listId: number, channel: string = 'instagram'): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(resetListAggregates());
  dispatch(setLoadingStatus(AGGREGATE_LOADING, true));

  try {
    const url = LISTS.getListAggregates(orgId, listId, channel);
    const response = await get(url);
    const aggregateData = response.data || {};

    dispatch(listAggregatesLoaded(listId, aggregateData));
  } catch (e) {
    logError(e);
  } finally {
    dispatch(setLoadingStatus(AGGREGATE_LOADING, false));
  }
};

export function duplicateCollection(
  currentOrgId: number,
  collectionId: number,
  organizationId: number,
  label: string,
  description: string
): ThunkAction {
  return async (dispatch: Dispatch): Promise<void> => {
    const url = LISTS.duplicateList(currentOrgId, collectionId);
    try {
      dispatch(showLoading(url));
      const response = await post(url, {
        data: { organizationId, label, description }
      }, {
        suppressGlobalNotification: true
      });

      const newCollectionId = response.data.id;
      dispatch(success(__t('lists_reducer.duplicate_list_success', {
        collectionUrl: `/collections/creators/${newCollectionId}?prev=lists&orgId=${organizationId}`
      })));
    } catch (error) {
      dispatch(danger(__t('lists_reducer.duplicate_list_error')));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export const extendExternalPageToken = async (orgId: number, listId: number) => {
  try {
    const url = CREATOR_PROPOSAL.extendExternalPageToken(orgId, listId);

    await put(url);
  } catch (error) {
    logError(error);
  }
};

export default createReducer<ListState, any>('lists', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET_LISTS]: () => initialState,
  [RESET_INFLUENCERS]: (state) => ({
    ...state,
    listInfluencers: [],
    isLoading: false
  }),
  [LOADING]: (state, { status }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      isLoading: status
    },
    isLoading: status
  }),
  [SAVING_SEARCH_TO_LIST]: (state, { status }) => ({
    ...state,
    isSavingToList: status
  }),
  [AGGREGATE_LOADING]: (state, { status }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      aggregateData: {
        ...state.currentList.aggregateData,
        isLoading: status
      }
    }
  }),
  [LIST_LOADED]: (state, { list }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      list,
      isLoading: false
    },
    isLoading: false
  }),
  [LIST_AGGREGATES_LOADED]: (state, { aggregateData }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      aggregateData: {
        ...state.currentList.aggregateData,
        ...aggregateData,
        isLoading: false
      }
    }
  }),
  [RESET_LIST_AGGREGATES]: (state) => ({
    ...state,
    currentList: {
      ...state.currentList,
      aggregateData: {
        ...initialState.currentList.aggregateData
      }
    }
  }),
  [DASHBOARD_LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [DASHBOARD_SORT_SEARCH_LOADING]: (state, { status }) => ({
    ...state,
    isSearchingOrSorting: status
  }),
  [DASHBOARD_LOADED]: (state, { results }) => ({
    ...state,
    all: results.data,
    screenState: {
      ...state.screenState,
      pagination: results.meta.pagination ? results.meta.pagination : {
        ...results.meta,
        links: results.links
      }
    },
    isLoading: false
  }),
  [UPDATED]: (state, { data, shouldUpdate }) => {
    let lists: Array<List> = [];

    if (shouldUpdate) {
      lists = [
        ...state.all.filter(list => data.id !== list.id),
        data
      ];
    } else {
      lists = [...state.all, data];
    }

    return {
      ...state,
      all: lists,
      currentList: {
        ...state.currentList,
        list: data,
        isLoading: false
      },
      isLoading: false
    };
  },
  [DELETED]: (state, { id }) => ({
    ...state,
    all: [...state.all.filter(list => id !== list.id)],
    isLoading: false
  }),
  [INFLUENCERS_LOADED]: (state, { data, loadMore }) => {
    let influencers;

    if (loadMore) {
      influencers = [
        ...state.currentList.influencers.filter(r => r.loading !== true), // return items without a loading property
        ...data
      ];
    } else {
      influencers = [
        ...data
      ];
    }

    return {
      ...state,
      currentList: {
        ...state.currentList,
        influencers,
        isLoading: false
      },
      isLoading: false
    };
  },
  [INFLUENCER_ADDED]: (state) => ({
    ...state,
    isLoading: false
    // In the future we might need to manipulate currentList here as well. Right now, users can not
    // add influencers to a list that is displayed on the same page, so this manipulation is
    // currently not necessary.
  }),
  [INFLUENCER_REMOVED]: (state, { influencerId, listId, currentListId }) => ({
    ...state,
    isLoading: false,
    currentList: {
      ...state.currentList,
      influencers: state.currentList.influencers.filter(influencer => !(Number(currentListId) === listId) || !(influencer.id === influencerId))
    }
  }),
  [SINGLE_INFLUENCER_REMOVED]: (state, { influencerId }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      influencers: state.currentList.influencers.filter(influencer => influencer.id !== influencerId)
    },
    isLoading: false
  }),
  [HISTORY_LOADED]: (state, { data }) => ({
    ...state,
    historyAvailability: {
      ...state.historyAvailability,
      ...data.historyAvailability
    },
    isLoading: false
  }),
  [UPDATE_SCREEN_STATE]: (state, { value, attribute }) => ({
    ...state,
    screenState: {
      ...state.screenState,
      [attribute]: value
    }
  }),
  [UPDATE_SCREEN_STATE_PAGINATION]: (state, { meta, links }) => ({
    ...state,
    screenState: {
      ...state.screenState,
      pagination: {
        ...meta,
        links
      }
    }
  }),
  [ADD_LOADING_ITEMS]: (state, { loadingItems }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      influencers: [
        ...state.currentList.influencers,
        ...loadingItems
      ]
    }
  }),
  [UPDATE_CREATED_PROFILE_SMARTLIST_ID]: (state, { id }) => ({
    ...state,
    myCreatedProfileSmartlistId: id
  }),
  [RESET_LOADING_ITEMS]: (state, { loadingItems }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      influencers: [
        ...loadingItems
      ]
    }
  }),
  [RESET_LOADING_ITEMS]: (state, { loadingItems }) => ({
    ...state,
    currentList: {
      ...state.currentList,
      influencers: [
        ...loadingItems
      ]
    }
  }),
  [UPDATE_LIGHTWEIGHT_INFLUENCERS_IMPORT_TASKS]: (
    state, { lightweightInfluencersImportTasks }
  ) => ({
    ...state,
    currentList: {
      ...state.currentList,
      list: {
        ...state.currentList.list,
        lightweightInfluencersImportTasks: [
          ...state.currentList.list.lightweightInfluencersImportTasks ?? [],
          ...lightweightInfluencersImportTasks
        ]
      }
    }
  })
});
